import styled from '@emotion/styled';
import React, { useState, ReactNode, Suspense, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { FLOATING_ROOT_ID } from '../../constants';
import { semantic_colors } from '../../foundation';
import { getZIndex } from '../../styles';
import { FloatingTreeContext } from '../context';
import { IconCheck, IconTriangleWarning, IconCircleWarning } from '../icons/generated';
import { NotificationStatus } from '../notification/types';
import { useProviderConfig } from '../provider';
import { Stack } from '../stack';
import { BaseText } from '../text';
import { ModalButtonProps, MIN_WIDTH } from './Modal';
import { ModalActionButtonType, ModalActionButton } from './ModalActionButton';
import { ModalOverlayBaseProps, ModalOverlay } from './ModalOverlay';
import { createUniqueIdentifier } from '../../utils/createUniqueIdentifier';

export interface AlertModalProps extends Omit<ModalOverlayBaseProps, 'onClose'> {
  /** @default 'info' */
  status?: NotificationStatus;
  title: ReactNode;
  content?: ReactNode;
  description?: ReactNode;
  dense?: boolean;
  confirmText?: string;
  confirmButtonProps?: ModalButtonProps;
  /**
   * autofocus 지정할 기본 버튼 요소
   * @default 'confirm'
   */
  autoFocusButton?: Extract<ModalActionButtonType, 'confirm'> | null;
  /**
   * confirmButton 클릭 이벤트
   */
  onConfirm?: () => void | Promise<void>;
  /**
   * onConfirm/onCancel이 호출된 직후 onClose가 호출됩니다.
   */
  onClose?: () => void | Promise<void>;
}
export interface ConfirmModalProps extends Omit<AlertModalProps, 'autoFocusButton'> {
  // 'cancelText' | 'onCancel' | 'autoFocusButton' | 'cancelButtonProps'
  cancelText?: string;
  /**
   * cancelButton 클릭 이벤트
   */
  onCancel?: () => void | Promise<void>;
  cancelButtonProps?: ModalButtonProps;
  /**
   * autofocus 지정할 기본 버튼 요소
   * @default 'confirm'
   */
  autoFocusButton?: ModalActionButtonType | null;
}

export function SimpleModalComponent<T extends ConfirmModalProps | AlertModalProps = ConfirmModalProps>({
  title,
  status = 'info',
  width = MIN_WIDTH,
  zIndex = getZIndex('modal'),
  dense = false,
  fill = false,
  content,
  description,
  confirmText,
  confirmButtonProps,
  cancelText,
  cancelButtonProps,
  autoFocusButton = 'confirm',
  onConfirm,
  onCancel,
  onClose,
  onExited,
  __floatingPortalId,
  ...props
}: CreateModalElementProps<T> & {
  cancelText?: ConfirmModalProps['cancelText'];
  cancelButtonProps?: ConfirmModalProps['cancelButtonProps'];
  onCancel?: ConfirmModalProps['onCancel'];
}) {
  const { locale } = useProviderConfig();
  const [isFreeze, setIsFreeze] = useState<boolean>(false);
  const [opened, setOpened] = useState<boolean>(false);

  const close = async () => {
    setOpened(false);
  };

  const handleConfirm = async () => {
    await onConfirm?.();
    flushSync(() => {
      setIsFreeze(true);
    });
    close();
  };

  const handleCancel = () => {
    onCancel?.();
    close();
  };

  const handleExit = () => {
    onClose?.();
    onExited?.();
  };

  const renderToIcon = () => {
    if (status === 'info') return;

    return (
      <Stack align='center' mb={4}>
        {status === 'success' && (
          <IconCheck size={24} color={semantic_colors.state.positive} aria-label={locale?.accessibility.success} />
        )}
        {status === 'warning' && (
          <IconTriangleWarning
            color={semantic_colors.state.warning}
            aria-label={locale?.accessibility.warning}
            size={24}
          />
        )}
        {status === 'error' && (
          <IconCircleWarning
            color={semantic_colors.state.negative}
            aria-label={locale?.accessibility.error}
            size={24}
          />
        )}
      </Stack>
    );
  };

  useEffect(() => {
    const timeout = setTimeout(() => setOpened(true), 0);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <ModalOverlay
      width={width}
      fill={fill}
      open={opened}
      setOpened={setOpened}
      onExited={handleExit}
      zIndex={zIndex}
      floatingPortalId={__floatingPortalId}
      autoFocus={autoFocusButton !== null}
      controlledOpen={true}
      onCancelEventCallback={close}
      {...props}
    >
      <Stack direction='column' p={dense ? 24 : 32} gap={dense ? 32 : 40}>
        <Stack direction='column' gap={8}>
          {renderToIcon()}
          <StyledText typography='Heading_20_Bold' color={semantic_colors.content.primary}>
            {title}
          </StyledText>
          {content && (
            <StyledText typography='Body_13_Regular' color={semantic_colors.content.primary}>
              {content}
            </StyledText>
          )}
          {description && (
            <StyledText typography='Body_13_Regular' color={semantic_colors.content.primary}>
              {description}
            </StyledText>
          )}
        </Stack>
        <Stack gap={8} ml='auto'>
          {cancelText && (
            <ModalActionButton autoFocus={autoFocusButton === 'cancel'} onClick={handleCancel} {...cancelButtonProps}>
              {cancelText}
            </ModalActionButton>
          )}
          <ModalActionButton
            disabled={isFreeze}
            autoFocus={autoFocusButton === 'confirm'}
            variant='filled'
            color='primary'
            onClick={handleConfirm}
            {...confirmButtonProps}
          >
            {confirmText}
          </ModalActionButton>
        </Stack>
      </Stack>
    </ModalOverlay>
  );
}

export const SimpleModal = FloatingTreeContext(SimpleModalComponent);

const StyledText = styled(BaseText)`
  white-space: pre-line;
`;

type CreateModalElementProps<T extends ConfirmModalProps | AlertModalProps> = T & { __floatingPortalId?: string };

export function createModalElement<T extends ConfirmModalProps | AlertModalProps = ConfirmModalProps | AlertModalProps>(
  properties: T,
) {
  let divTarget = document.getElementById(FLOATING_ROOT_ID);
  if (!divTarget) {
    divTarget = document.createElement('div');
    divTarget.id = FLOATING_ROOT_ID;
    document.body.appendChild(divTarget);
  }
  const divRoot = document.createElement('div');
  divRoot.id = `${FLOATING_ROOT_ID}-${'cancelText' in properties ? 'confirm' : 'alert'}-${createUniqueIdentifier()}`;
  divTarget.appendChild(divRoot);

  const root = createRoot(divRoot);
  root.render(
    <Suspense fallback={null}>
      <SimpleModal
        {...properties}
        __floatingPortalId={divRoot.id}
        onExited={() => {
          properties.onExited?.();
          divRoot.remove();
          root.unmount();
        }}
      />
    </Suspense>,
  );
}
